import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { SITE, NAV } from "../lib/site";
import { useT } from "../lib/i18n";
import LanguageToggle from "./LanguageToggle";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#E5EBE5] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <a href="#accueil" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={SITE.logo}
              alt={`Logo ${SITE.name}`}
              width="48"
              height="48"
              className="h-12 w-12 rounded-full object-cover ring-2 ring-[#E8F5E9]"
            />
            <div className="hidden sm:block">
              <div className="font-['Outfit'] font-semibold text-[#1B5E20] leading-tight">
                K'REUSE
              </div>
              <div className="text-xs tracking-widest text-[#5C705E] uppercase">
                Extérieur
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-4 py-2 text-sm font-medium text-[#1A2E1C] hover:text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full transition-all"
              >
                {t(`nav.${item.label}`)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageToggle />
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-facebook"
              aria-label="Page Facebook K'REUSE EXTÉRIEUR"
              className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-[#1877F2] hover:bg-[#0e63d6] text-white transition-all hover:-translate-y-0.5 shadow-md"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M24 12a12 12 0 1 0-13.88 11.85v-8.38h-3.05V12h3.05V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.69.23 2.69.23v2.95h-1.52c-1.49 0-1.95.93-1.95 1.88V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0 0 24 12Z" />
              </svg>
            </a>
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="header-phone"
              className="hidden md:flex items-center gap-2 text-sm font-medium text-[#1B5E20] hover:underline"
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
            <Button
              asChild
              data-testid="header-cta-devis"
              className="hidden md:inline-flex bg-[#1B5E20] hover:bg-[#164d1a] text-white rounded-full px-6"
            >
              <a href="/#contact">{t("header.devis")}</a>
            </Button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-full hover:bg-[#E8F5E9]"
              onClick={() => setOpen((s) => !s)}
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-6 pt-2 border-t border-[#E5EBE5] bg-white/95 backdrop-blur-xl -mx-6 md:-mx-12 px-6 md:px-12">
            <nav className="flex flex-col gap-1 mt-3">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="px-4 py-3 text-base font-medium text-[#1A2E1C] hover:bg-[#E8F5E9] rounded-xl"
                >
                  {t(`nav.${item.label}`)}
                </a>
              ))}
              <div className="px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-[#5C705E]">{t("langToggle.current")}</span>
                <LanguageToggle variant="compact" />
              </div>
              <a
                href={`tel:${SITE.phoneRaw}`}
                className="px-4 py-3 mt-2 bg-[#1B5E20] text-white rounded-xl flex items-center justify-center gap-2 font-medium"
              >
                <Phone className="w-4 h-4" />
                {SITE.phone}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
